import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Polygon, Marker, Popup, useMap, useMapEvents, LayersControl, LayerGroup, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { useLocalization } from '../Context/MultiLanguageContext';
import { useNavigate } from "react-router-dom";
import { getPamCodeSuggessionAsync } from '../../API/Address/GetPamCodeSuggessionAsync';
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";
import MarkerTooltip from "./MarkerTooltip";
import { Address as PamAddress } from '../../API/Address/GetPamCodeSuggessionAsync';
import ViewModal from './ViewModal';
import * as turf from "@turf/turf";

interface TokenPayload {
  RoleId: number;
}

interface BoundaryPoint {
  latitude: number;
  longitude: number;
}

interface PolygonMapComponentProps {
  addresses?: PamAddress[];
  boundaryData?: BoundaryPoint[] | BoundaryPoint[][];
}

function getDistanceInMeters(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = toRadians(lat1);
  const φ2 = toRadians(lat2);
  const Δφ = toRadians(lat2 - lat1);
  const Δλ = toRadians(lon2 - lon1);

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) *
    Math.sin(Δλ / 2) * Math.sin(Δλ / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  const distance = R * c;
  return distance; // in meters
}

function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}

function getPolygonLengthBreadth(polygon: [number, number][]) {
  if (polygon.length < 2) return { length: 0, breadth: 0 };
  let minLat = polygon[0][0], maxLat = polygon[0][0];
  let minLng = polygon[0][1], maxLng = polygon[0][1];
  polygon.forEach(([lat, lng]) => {
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
  });
  // Length: north-south (lat), Breadth: east-west (lng)
  const length = getDistanceInMeters(minLat, minLng, maxLat, minLng);
  const breadth = getDistanceInMeters(minLat, minLng, minLat, maxLng);
  return {
    length: parseFloat((length / 1000).toFixed(3)), // Convert to km
    breadth: parseFloat((breadth / 1000).toFixed(3)) // Convert to km
  };
}

const PolygonMapComponent: React.FC<PolygonMapComponentProps> = ({ addresses = [], boundaryData = [] }) => {
  const [marker, setMarker] = useState<L.LatLng | null>(null);
  const [markerPosition, setMarkerPosition] = useState<[number, number] | null>(null);
  const [street, setStreet] = useState<string>("");
  const [city, setCity] = useState<string>("");
  const [pincode, setPincode] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useLocalization();
  const navigate = useNavigate();
  const [addressResponse, setAddressResponse] = useState<PamAddress[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeSharePamCode, setActiveSharePamCode] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<{ type: "pamCode" | "digiPin"; value: string } | null>(null);

  const [roleId, setRoleId] = useState<number | null>(null);

  const defaultCenter: [number, number] =
    addresses.length > 0
      ? [addresses[0].latitude, addresses[0].longitude]
      : [9.9921, 76.3019];

  const polygonPositions = addresses.map(address =>
    L.latLng(address.latitude, address.longitude)
  );
  const boundaryRings: BoundaryPoint[][] =
    boundaryData.length > 0 && Array.isArray(boundaryData[0])
      ? boundaryData as BoundaryPoint[][]
      : [boundaryData as BoundaryPoint[]];
  const boundaryPositions = boundaryRings.flatMap(ring =>
    ring.map(boundary => L.latLng(boundary.latitude, boundary.longitude))
  );

  // ✅ Calculate polygon area if boundaryData exists
  const [polygonArea, setPolygonArea] = useState<number | null>(null);

  useEffect(() => {
    if (boundaryRings.some(ring => ring.length > 2)) {
      const polygonCoords = boundaryRings[0].map(b => [b.longitude, b.latitude]);

      // Close the polygon loop
      if (
        polygonCoords[0][0] !== polygonCoords[polygonCoords.length - 1][0] ||
        polygonCoords[0][1] !== polygonCoords[polygonCoords.length - 1][1]
      ) {
        polygonCoords.push(polygonCoords[0]);
      }

      const polygon = turf.polygon([polygonCoords]);
      const areaSqMeters = turf.area(polygon);
      const areaSqKm = areaSqMeters / 1_000_000;
      setPolygonArea(areaSqKm);
      // console.log(`Polygon Area: ${areaSqKm.toFixed(2)} sq km`);
    } else {
      setPolygonArea(null);
    }
  }, [boundaryRings]);


  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decodedToken = jwtDecode<TokenPayload>(token);
        setRoleId(Number(decodedToken.RoleId));
      } catch (error) {
        // console.error('Error decoding token:', error);
      }
    }
  }, []);

  useEffect(() => {
  }, [roleId]);

  useEffect(() => {
    if (!markerPosition) return;
    const [lat, lng] = markerPosition;
    fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`)
      .then(response => response.json())
      .then(data => {
        setPincode(data.address?.postcode || "N/A");
        setStreet(data.address?.road || "N/A");
        setCity(data.address?.city || data.address?.town || data.address?.village || "N/A");
      })
      .catch(() => {
        setPincode("");
        setStreet("");
        setCity("");
      });
  }, [markerPosition]);

  useEffect(() => {
    if (!isModalOpen) {
      setActiveSharePamCode(null);
    }
  }, [isModalOpen]);


  const fetchAddresses = async () => {
    setIsSubmitting(true);
    const token = localStorage.getItem("token");
    if (!token) {
      // console.error("Token not found");
      setIsSubmitting(false);
      return;
    }
    try {
      const data = await getPamCodeSuggessionAsync(
        markerPosition ? markerPosition[0] : 0,
        markerPosition ? markerPosition[1] : 0,
        token,
        t
      );
      if (data === null) {
        setIsSubmitting(false);
        return;
      }
      // console.log("Fetched addresses:", data);
      if (Array.isArray(data) && data.length > 0) {
        // Data is an array of addresses — set it directly.
        setAddressResponse(data as PamAddress[]);
      } else if (data && !Array.isArray(data)) {
        // If API returns a single address object, normalize it into an array.
        setAddressResponse([data as PamAddress]);
      } else {
        console.warn("No data returned from PAM Code suggestions API or data has unexpected shape:", data);
        setAddressResponse([]);
      }

      setIsSubmitting(false);
      setIsModalOpen(true);
    } catch (error) {
      // console.error("Error fetching addresses:", error);
      setAddressResponse([]);
      setIsSubmitting(false);
    }
  };

  const handleCopy = async (value: string, type: "pamCode" | "digiPin") => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField({ type, value });
      setTimeout(() => setCopiedField(null), 2000);

    } catch (err) {
      // console.error('Failed to copy: ', err);

    }
  };

  const formatPamCode = (pamCode: string) => {
    if (!pamCode) return "N/A";
    const pincode = pamCode.slice(0, 6);
    const blockCode = pamCode.slice(6, 10);
    const addressCode = pamCode.slice(10);
    return `${pincode}-${blockCode}-${addressCode}`;
  };
  const shareMessage = (address: PamAddress) => {
    return `Check out this address:
    ${address.addressLine1}, ${address.addressLine2}, ${address.addressLine3}
    PAM Code: ${formatPamCode(address.pamCode || "")}
    DIGI PIN: ${address.digiPin || "N/A"}
    
    More details: https://getmethere.in`;
  };

  function shareBlockCodeMessage(pinCode: string, blockCode: string, digiPin?: string): string {
    return `Block Code: ${pinCode}-${blockCode}
DIGI PIN: ${digiPin || "N/A"}

More details: https://getmethere.in`;
  }
  const goToMapAddAddress = (latitude: number, longitude: number) => {
    if (roleId === 3) {
      toast.info("Please Log In to Add Address", { toastId: "guest-login" });
      return;
    }
    const params = new URLSearchParams({
      latitude: latitude.toString(),
      longitude: longitude.toString(),
      addressLine1: street || '',
      addressLine2: city || '',
      addressLine3: `${street}, ${city}` || '',
      pinCode: pincode || '',
    });
    navigate(`/add-new-address?${params.toString()}`);
  };

  const MapClickHandler = () => {
    useMapEvents({
      click(e) {
        setMarker(L.latLng(e.latlng.lat, e.latlng.lng));
        setMarkerPosition([e.latlng.lat, e.latlng.lng]);
      }
    });
    return null;
  };

  const handleMarkerClick = (lat: number, lng: number) => {
    const googleMapsUrl = `https://www.google.com/maps?q=${lat},${lng}`;
    window.open(googleMapsUrl, '_blank');
  };

  const hasFitBounds = useRef(false);

  const FitBoundsHandler = () => {
    const map = useMap();

    useEffect(() => {
      if (hasFitBounds.current) return;

      // Fit the complete returned area, including all locality rings.
      if (boundaryPositions.length > 0) {
        const bounds = L.latLngBounds(boundaryPositions);
        map.fitBounds(bounds, {
          padding: [30, 30],
          maxZoom: 16,
        });
        hasFitBounds.current = true;
        return;
      }

      // For address markers
      if (polygonPositions.length > 0) {
        const bounds = L.latLngBounds(polygonPositions);
        map.fitBounds(bounds, {
          padding: [30, 30],
        });
        hasFitBounds.current = true;
      }
    }, [map, boundaryPositions, polygonPositions]);

    return null;
  };

  useEffect(() => {
    hasFitBounds.current = false;
  }, [addresses, boundaryData]);

  const openGoogleMaps = (latitude: number, longitude: number) => {
    const defaultMap = localStorage.getItem("defaultMap") || "google";

    if (defaultMap === "waze") {
      const wazeMapsUrl = `https://waze.com/ul?ll=${latitude},${longitude}&navigate=yes`;
      window.open(wazeMapsUrl, '_blank');
    } else if (defaultMap === "mapindia") {
      const mapplsUrl = `https://www.mappls.com/@${latitude},${longitude}`;
      window.open(mapplsUrl, "_blank");
    } else if (defaultMap === "apple") {
      const appleMapsUrl = `https://maps.apple.com/?ll=${latitude},${longitude}&q=${latitude},${longitude}&z=18&t=m`;
      window.open(appleMapsUrl, "_blank");
    } else {
      const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
      window.open(googleMapsUrl, '_blank');
    }
  };
  // Add a dummy toggleFavourite function (update as needed)
  const toggleFavourite = (id?: string) => {
    // Implement your logic here, e.g., update addressResponse state
    // For now, just log the id
    // console.log("Toggle favourite for address id:", id);
  };

  const HeartClick = (addressId: string, isFavourite: boolean) => {
    // Implement your logic here, e.g., update addressResponse state or call an API
    // For now, just log the action
    // console.log("HeartClick for address id:", addressId, "isFavourite:", isFavourite);
  };

  return (
    <div style={{ height: '100%', width: '100%' }}>
      <MapContainer center={defaultCenter} zoom={13} className="h-full w-full">
        <LayersControl position="topright">
          <LayersControl.BaseLayer name={t("defaultView")} checked>
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              maxNativeZoom={19}
              maxZoom={22}
              attribution='&copy; OpenStreetMap contributors'
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name={t("satelliteView")}>
            <LayerGroup>
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                maxNativeZoom={19}
                maxZoom={22}
                attribution="&copy; Esri"
              />
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                attribution="&copy; Esri"
                opacity={0.8}
              />
            </LayerGroup>
          </LayersControl.BaseLayer>
        </LayersControl>

        {polygonPositions.length > 2 && (
          <Polygon positions={polygonPositions} color="" opacity={0.5} fillOpacity={0} />
        )}

        {boundaryRings.map((ring, index) => {
          const positions = ring.map(boundary =>
            L.latLng(boundary.latitude, boundary.longitude)
          );

          return positions.length > 0 && (
            <React.Fragment key={`boundary-${index}`}>
              <Polygon positions={positions} color="blue" opacity={0.5} fillOpacity={0.1} />
              {ring.length > 2 && (
                <PolygonAreaDisplay
                  boundaryData={ring}
                />
              )}
            </React.Fragment>
          );
        })}


        <MapClickHandler />
        <FitBoundsHandler />

        {marker && (
          <Marker position={marker} eventHandlers={{ click: () => handleMarkerClick(marker.lat, marker.lng) }}>
            <Popup>
              <span>Click to open in Google Maps</span>
            </Popup>
          </Marker>
        )}

        {markerPosition && (
          <Marker position={markerPosition}>
            <MarkerTooltip
              street={street}
              city={city}
              pincode={pincode}
              markerPosition={markerPosition}
              isSubmitting={isSubmitting}
              t={t}
              openGoogleMaps={(lat, lng) => {
                const url = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
                window.open(url, "_blank");
              }}
              goToMapAddAddress={goToMapAddAddress}
              fetchAddresses={fetchAddresses}
              onClose={() => setMarkerPosition(null)}
            />
          </Marker>
        )}
      </MapContainer>


      <ViewModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        addressResponse={addressResponse}
        t={t}
        copiedField={copiedField}
        handleCopy={handleCopy}
        activeSharePamCode={activeSharePamCode}
        setActiveSharePamCode={setActiveSharePamCode}
        toggleFavourite={toggleFavourite}
        formatPamCode={formatPamCode}
        shareMessage={shareMessage}
        shareBlockCodeMessage={shareBlockCodeMessage}
      />
    </div>
  );
};

// Add this new component at the end of the file, before export
const PolygonAreaDisplay: React.FC<{
  boundaryData: { latitude: number; longitude: number }[];
}> = ({ boundaryData }) => {
  const map = useMap();
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const { t } = useLocalization();
  const dimensions = getPolygonLengthBreadth(
    boundaryData.map(point => [point.latitude, point.longitude])
  );

  useEffect(() => {
    if (!map || boundaryData.length < 1) return;

    // Find the topmost point (max latitude)
    let topPoint = boundaryData[0];
    for (const pt of boundaryData) {
      if (pt.latitude > topPoint.latitude) topPoint = pt;
    }

    const updatePos = () => {
      const containerPoint = map.latLngToContainerPoint([topPoint.latitude, topPoint.longitude]);
      setPos({ left: containerPoint.x, top: containerPoint.y });
    };

    updatePos();
    map.on('move zoom', updatePos);

    return () => {
      map.off('move zoom', updatePos);
    };
  }, [map, boundaryData]);

  if (!pos || dimensions.length === 0 || dimensions.breadth === 0) return null;

  return (
    <div
      style={{
        position: 'absolute',
        left: pos.left,
        top: pos.top,
        color: "black",
        padding: "4px 10px",
        borderRadius: "6px",
        fontWeight: 600,
        fontSize: "12px",
        minWidth: 'unset',
        textAlign: 'center',
        lineHeight: 1.2,
        zIndex: 1000,
        pointerEvents: 'none',
        transform: 'translate(-50%, -100%)',
        background: "transparent",
      }}
    >
      <span>{t("area")}</span>: {dimensions.length} x {dimensions.breadth} km²
    </div>
  );
};

export default PolygonMapComponent;
